/**
 * Machine-lookup drawer body (boards/Lookup.dc.html). Full SN or a ≥4-char tail →
 * GET /api/machine-lookup → either a match list or one MachineDetail: status + derived origin,
 * WHY it counts as that origin, owner, roster dates, import batch, the audit trail and the
 * reporter's tickets. Writes (revoke / restore / expiry / owner) go through the same audited
 * roster endpoints as the table.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from './Icon';
import { OriginTag, StatusTag, ToneTag } from './RosterBits';
import { ExpiryModal, MetaModal, TypeConfirmModal, errMsg } from './Dialogs';
import { api } from '@/lib/api';
import { fmtDate, fmtDay, shortMaskedCode } from '@/lib/format';
import { utcDay } from '@/lib/dates';
import { useDebounced } from '@/lib/useDebounced';
import { SEARCH_TOO_LONG, lookupTooLong, normalizeLookup } from '@/lib/searchLimit';
import {
  ORIGIN_LABEL,
  SOURCE_LABEL,
  ticketStatusLabel,
  ticketStatusTone,
} from '@/lib/labels';
import { auditActor, auditSentence } from '@/lib/auditText';
import type { MachineDetail, MachineLookupResult } from '@/types';

const MIN_QUERY = 4;

export function LookupDrawer({
  initialQuery, onClose, onChanged,
}: {
  initialQuery: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState(initialQuery);
  // Worker accepts 4–64 chars of [A-Z0-9-]; drop pasted spaces / stray punctuation. Longer than 64
  // is refused here (never silently cut to a different SN).
  const normalized = normalizeLookup(q);
  const tooLong = lookupTooLong(normalized);
  const debounced = useDebounced(tooLong ? '' : normalized, 300);
  const [result, setResult] = useState<MachineLookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (debounced.length < MIN_QUERY) { setResult(null); setErr(null); return; }
    let alive = true;
    setLoading(true);
    api.machineLookup(debounced)
      .then((r) => { if (alive) { setResult(r); setErr(null); } })
      .catch((e) => { if (alive) { setResult(null); setErr(errMsg(e)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [debounced, reload]);

  // Esc closes the drawer — unless a dialog opened from it is on top (it handles its own Esc).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.modal-backdrop')) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const changed = useCallback(() => { setReload((n) => n + 1); onChanged(); }, [onChanged]);

  const go = (to: string) => { onClose(); navigate(to); };
  const machine = result?.machine ?? null;
  const matches = result?.matches ?? [];

  let hint = '輸入完整硬體序號或末四碼';
  if (debounced.length > 0 && debounced.length < MIN_QUERY) hint = `至少輸入 ${MIN_QUERY} 碼`;
  else if (loading && !result) hint = '查詢中…';
  else if (result) {
    const kind = debounced.length <= 6 ? `末四碼「${debounced}」` : `「${debounced}」`;
    hint = matches.length === 0 && !machine ? `${kind}找不到符合的機器` : `${kind}符合 ${Math.max(matches.length, machine ? 1 : 0)} 台`;
  }

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <section className="drawer" role="dialog" aria-modal="true" aria-label="機器詳情">
        <div className="drawer-head">
          <label className="drawer-search">
            <Icon name="search" size={16} />
            <input
              ref={inputRef}
              aria-label="硬體序號或末四碼"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="硬體序號或末四碼"
              spellCheck={false}
              autoComplete="off"
            />
          </label>
          <button type="button" className="drawer-close" aria-label="關閉" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="drawer-hint" role="status">
          {tooLong ? <span className="error-line">{SEARCH_TOO_LONG}</span> : err ? <span className="error-line">查詢失敗:{err}</span> : hint}
        </div>

        <div className="drawer-body">
          {!machine && matches.length > 0 && (
            <div className="match-list">
              {matches.map((m) => (
                <button key={m.sn} type="button" className="match-item" onClick={() => setQ(m.sn)}>
                  <span className="mono">{m.sn}</span>
                  <StatusTag status={m.status} />
                  <OriginTag origin={m.origin} />
                  <span className="muted">{m.owner_name || '—'}</span>
                </button>
              ))}
            </div>
          )}
          {machine && <MachineView machine={machine} onGo={go} onChanged={changed} />}
        </div>

        {machine && <DrawerFooter machine={machine} onGo={go} onChanged={changed} />}
      </section>
    </>
  );
}

function whyCard(m: MachineDetail): { title: string; line: React.ReactNode; sub: string } {
  const e = m.entry;
  const title = `為什麼算「${ORIGIN_LABEL[e.origin] ?? e.origin}」`;
  switch (e.origin) {
    case 'serial': {
      const lic = [...m.licenses]
        .filter((l) => l.activated_at)
        .sort((a, b) => (b.activated_at ?? 0) - (a.activated_at ?? 0))[0];
      return {
        title,
        line: <>軟體序號 <span className="mono" title={lic?.code_masked ?? e.license_masked ?? undefined}>{shortMaskedCode(lic?.code_masked ?? e.license_masked)}</span> 綁定這台</>,
        sub: lic ? `兌換 ${fmtDay(lic.activated_at)} · 批次 ${lic.batch_id}` : '已兌換',
      };
    }
    case 'import':
      return {
        title,
        line: <>由匯入批次加入</>,
        sub: m.import_batch ? `${m.import_batch.name} · ${fmtDate(m.import_batch.created_at)}` : e.import_batch_name ?? '—',
      };
    case 'manual':
      return { title, line: <>由後台手動加入</>, sub: `${e.added_by ?? '—'} · ${fmtDate(e.added_at)}` };
    case 'machine_approval':
      return { title, line: <>由後台核准機器加入</>, sub: `${e.added_by ?? '—'} · ${fmtDate(e.added_at)}` };
    default:
      return { title, line: <>加入方式沒有紀錄</>, sub: '升級前加入、且沒有綁定的軟體序號' };
  }
}

function MachineView({ machine, onGo, onChanged }: { machine: MachineDetail; onGo: (to: string) => void; onChanged: () => void }) {
  const e = machine.entry;
  const [editMeta, setEditMeta] = useState(false);
  const why = whyCard(machine);
  const owner = [e.owner_name, e.team].filter(Boolean).join(' · ') || '—';
  const events = [...machine.audit].sort((a, b) => (a.ts ?? 0) - (b.ts ?? 0));

  return (
    <>
      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        <div className="machine-sn">{e.sn}</div>
        <div className="row" style={{ gap: 'var(--space-2)' }}>
          <StatusTag status={e.status} />
          <OriginTag origin={e.origin} />
          {e.revoked_reason && <ToneTag tone="neutral">撤銷原因:{e.revoked_reason}</ToneTag>}
        </div>
      </div>

      <div className="info-grid">
        <div className="info-card">
          <div className="k">{why.title}</div>
          <div className="v">{why.line}</div>
          <div className="s one-line" title={why.sub}>{why.sub}</div>
        </div>
        <div className="info-card">
          <div className="k">擁有人</div>
          <div className="v">{owner}</div>
          <div className="s">
            {e.owner_email || '沒有 Email'} · <button type="button" className="link" onClick={() => setEditMeta(true)}>編輯</button>
          </div>
        </div>
        <div className="info-card">
          <div className="k">加入名單</div>
          <div className="v">{fmtDate(e.added_at)}</div>
          <div className="s">到期:{e.expires_at ? utcDay(e.expires_at) : '無'}</div>
        </div>
        <div className="info-card">
          <div className="k">匯入批次</div>
          <div className="v">
            {machine.import_batch || e.import_batch_id ? (
              <button
                type="button"
                className="link one-line"
                title={machine.import_batch?.name ?? e.import_batch_name ?? e.import_batch_id ?? undefined}
                onClick={() => onGo(`/roster?batch=${encodeURIComponent(e.import_batch_id ?? machine.import_batch?.id ?? '')}`)}
              >
                {machine.import_batch?.name ?? e.import_batch_name ?? e.import_batch_id}
              </button>
            ) : '—'}
          </div>
          <div className="s">{machine.import_batch ? `${fmtDate(machine.import_batch.created_at)} · ${machine.import_batch.created_by}` : e.import_batch_id ? '' : '不是由匯入加入'}</div>
        </div>
      </div>

      {e.notes && (
        <div className="info-card"><div className="k">備註</div><div className="v">{e.notes}</div></div>
      )}

      <div className="stack" style={{ gap: 10 }}>
        <h2 className="section-title">紀錄</h2>
        {events.length === 0 ? (
          <span className="muted-note">沒有紀錄</span>
        ) : (
          events.map((a) => (
            <div key={a.id} className="event-row">
              <span className="at">{fmtDate(a.ts)}</span>
              <span>
                {a.actor_type === 'app' ? (
                  <span title={auditActor(a).title}>{auditActor(a).text}</span>
                ) : (
                  <>{SOURCE_LABEL[a.actor_type] ?? a.actor_type}{a.actor_id ? ` ${a.actor_id}` : ''}</>
                )}{' '}
                · {auditSentence(a, { withTarget: false })}
              </span>
            </div>
          ))
        )}
      </div>

      <div className="stack" style={{ gap: 10 }}>
        <h2 className="section-title">客服工單</h2>
        {machine.tickets.length === 0 ? (
          <span className="muted-note">沒有從這台送出的回報</span>
        ) : (
          machine.tickets.map((t) => (
            <button key={t.id} type="button" className="ticket-line" style={{ background: 'var(--surface)', font: 'inherit', textAlign: 'left', cursor: 'pointer' }} onClick={() => onGo(`/support?ticket=${encodeURIComponent(t.id)}`)}>
              <ToneTag tone={ticketStatusTone(t.status)}>{ticketStatusLabel(t.status)}</ToneTag>
              <span className="grow">{t.subject}</span>
              <span className="muted">{fmtDay(t.created_at)}</span>
            </button>
          ))
        )}
      </div>

      {editMeta && (
        <MetaModal
          open
          sn={e.sn}
          initial={{ owner_name: e.owner_name ?? '', owner_email: e.owner_email ?? '', team: e.team ?? '', notes: e.notes ?? '' }}
          onCancel={() => setEditMeta(false)}
          onSave={async (patch) => { await api.setRosterMeta(e.sn, patch); setEditMeta(false); onChanged(); }}
        />
      )}
    </>
  );
}

function DrawerFooter({ machine, onGo, onChanged }: { machine: MachineDetail; onGo: (to: string) => void; onChanged: () => void }) {
  const e = machine.entry;
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [expiryOpen, setExpiryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const blocked = e.status === 'revoked' || e.status === 'rejected';

  const restore = async () => {
    setBusy(true); setErr(null);
    try { await api.restoreWhitelist(e.sn); onChanged(); }
    catch (x) { setErr(errMsg(x)); }
    finally { setBusy(false); }
  };

  return (
    <div className="drawer-foot">
      {blocked ? (
        <button type="button" className="btn btn-outline" disabled={busy} onClick={() => void restore()}>復原這台</button>
      ) : (
        <button type="button" className="btn btn-outline-danger" onClick={() => setRevokeOpen(true)}>撤銷這台…</button>
      )}
      <button type="button" className="btn btn-outline" onClick={() => setExpiryOpen(true)}>設定到期日</button>
      <span className="grow">{err && <span className="error-line">{err}</span>}</span>
      <button type="button" className="btn btn-secondary" onClick={() => onGo(`/audit?q=${encodeURIComponent(e.sn)}`)}>完整稽核紀錄</button>

      <TypeConfirmModal
        key={`revoke-${e.sn}-${revokeOpen}`}
        open={revokeOpen}
        title="撤銷這台機器"
        body={<>撤銷後這台機器無法通過名單准入,也不能再以軟體序號自行恢復;只有後台能復原。請輸入硬體序號確認。</>}
        expect={e.sn}
        confirmLabel="撤銷"
        reasonLabel="撤銷原因(選填,寫入稽核)"
        onCancel={() => setRevokeOpen(false)}
        onConfirm={async (reason) => { await api.revokeWhitelist(e.sn, reason || undefined); setRevokeOpen(false); onChanged(); }}
      />
      {expiryOpen && (
        <ExpiryModal
          open
          sn={e.sn}
          current={e.expires_at}
          onCancel={() => setExpiryOpen(false)}
          onSave={async (v) => { await api.setRosterExpiry(e.sn, v); setExpiryOpen(false); onChanged(); }}
        />
      )}
    </div>
  );
}
